/**
 * @file base.js
 * @description Base of the theme scripts for the Shopify Theme-Inspire.
 * @copyright 2025 @InStijlTech
 *
 * This file serves as the entry point for initializing various theme scripts.
 * It imports and initializes different modules required for the theme's functionality.
 *
 * Base of the theme scripts
 * Import your scripts here
 * Example: import { log_jj } from './js-log-functions.js';
 *
 */
console.log("BASE TEST KTDH v3");

import { ismInitSwipers, ismSwiperChange } from './js-carousel.js';
import { ismInitMobileNav, ismInitDesktopNav } from './js-navigation.js';
import {
  initProductCardHover,
  initProductOptions,
  initRecommendations,
  initProductCardOptions,
  initProductAddToCart,
} from './js-product.js?v=producttest3';
import { initLanguageSelector } from './js-language-selector.js';
import { addAnimationOnScroll } from './js-animate.js';
import { initFilters } from './js-filters.js';
import { initSearch } from './js-search.js';

/**
 * Generic smooth scroll utility function
 * @param {string} targetSelector - CSS selector for the target element
 * @param {number} offset - Offset in pixels (default: 100)
 */
export const smoothScrollTo = (targetSelector, offset = 100) => {
  const target = document.querySelector(targetSelector);
  if (!target) return;

  const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  });
};

/**
 * Initialize smooth scroll for all links with data-smooth-scroll attribute
 * Usage: <a href="#target" data-smooth-scroll>Link</a>
 */
const initSmoothScroll = () => {
  const smoothScrollLinks = document.querySelectorAll('[data-smooth-scroll]');

  smoothScrollLinks.forEach((link) => {
    link.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (!href || !href.startsWith('#')) return;

      e.preventDefault();

      const offset = document.getElementsByClassName('ism-mainHeaderSection')[0].offsetHeight;

      smoothScrollTo(href, offset);
    });
  });
};

const ISM_init = () => {
  // Hide cookie banner in dev mode
  if (window.location.href.includes('http://127.0.0.1:9292/')) {
    setTimeout(() => {
      const cookieBanner = document.getElementById('shopify-pc__banner');
      if (cookieBanner) {
        cookieBanner.style.display = 'none';
      }
    }, 500);
  }

  ismInitSwipers();
  ismInitMobileNav();
  ismInitDesktopNav();
  ismSwiperChange();
  initProductCardHover();
  initProductOptions();
  initProductCardOptions();
  initProductAddToCart();
  initFilters();
  initRecommendations();
  addAnimationOnScroll();
  initSearch();
  initLanguageSelector();
  initSmoothScroll();
};

document.addEventListener('DOMContentLoaded', ISM_init);

// Shopify design mode
const hideModalsInDesignMode = (event) => {
  const modal = event.target.querySelector('.modal');
  if (modal) {
    const bsModal = bootstrap.Modal.getInstance(modal);
    if (bsModal) {
      bsModal.hide();
    }
  }

  const offcanvas = event.target.querySelector('.offcanvas');
  if (offcanvas) {
    const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvas);
    if (bsOffcanvas) {
      bsOffcanvas.hide();
    }
  }
};

const showModalsInDesignMode = (event) => {
  const modal = event.target.querySelector('.modal');
  if (modal) {
    const bsModal = new bootstrap.Modal(modal);
    bsModal.show();
  }

  const offcanvas = event.target.querySelector('.offcanvas');
  if (offcanvas) {
    const bsOffcanvas = new bootstrap.Offcanvas(offcanvas);
    bsOffcanvas.show();
  }
};

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    console.info('Shopify Section Loaded', event);
    ISM_init();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    hideModalsInDesignMode(event);
  });

  document.addEventListener('shopify:section:select', (event) => {
    setTimeout(() => {
      showModalsInDesignMode(event);
    }, 500);
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    hideModalsInDesignMode(event);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const swiperEl = block.closest('.swiper');

    if (swiperEl && swiperEl.swiper) {
      const swiper = swiperEl.swiper;
      const slideIndex = Array.from(swiperEl.querySelectorAll('.swiper-slide')).indexOf(block);

      if (slideIndex !== -1) {
        swiper.slideTo(slideIndex);
      }
    }
  });
}
